/**
 * Lead Database
 * Enterprise-grade table view for all leads with bulk actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { downloadFile } from '@/lib/downloadFile';
import EnrollInSequenceDialog from '@/campaigns/EnrollInSequenceDialog';
import {
  Search,
  Download,
  Target,
  Filter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Mail,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// GET /phase9/leads already supports these filters; the table only ever sent
// keyword + sort. Empty string = filter off.
const EMPTY_FILTERS = { score_label: '', has_website: '', min_rating: '', city: '', state: '' };
const SELECT_CLASS = 'bg-white/5 border border-white/10 text-white rounded-md px-3 py-2';

const LeadDatabase = ({ user, onNavigate }) => {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [markingFollowUp, setMarkingFollowUp] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  useEffect(() => {
    if (user?.role !== 'Master Administrator') return;
    fetch(`${API}/team`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setTeamMembers(data || []))
      .catch(() => {});
  }, [user?.role, authHeaders]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('keyword', searchQuery);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('skip', (page - 1) * limit);
      params.append('limit', limit);

      const response = await fetch(`${API}/phase9/leads?${params}`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      } else {
        toast.error('Failed to fetch leads');
      }
    } catch (error) {
      toast.error('Error fetching leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, searchQuery, filters, sortBy, sortOrder, page, limit]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleBulkScore = async () => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to score');
      return;
    }
    try {
      const response = await fetch(`${API}/phase9/leads/bulk-score`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ data: { lead_ids: selectedLeads } })
      });
      if (response.ok) {
        toast.success(`${selectedLeads.length} leads scored successfully`);
        setSelectedLeads([]);
        fetchLeads();
      } else {
        toast.error('Bulk scoring failed');
      }
    } catch (error) {
      toast.error('Error during bulk scoring');
    }
  };

  const handleBulkAssign = async () => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to assign');
      return;
    }
    if (!assigneeId) {
      toast.error('Choose a team member to assign to');
      return;
    }
    setAssigning(true);
    try {
      const response = await fetch(`${API}/lead-acquisition/bulk-action`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ record_ids: selectedLeads, action: 'assign_leads', payload: { assigned_team_member_id: assigneeId } })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.updated} lead(s) assigned`);
        setSelectedLeads([]);
        setAssigneeId('');
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Bulk assignment failed');
      }
    } catch (error) {
      toast.error('Error during bulk assignment');
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkFollowUp = async () => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to mark for follow-up');
      return;
    }
    setMarkingFollowUp(true);
    try {
      const response = await fetch(`${API}/lead-acquisition/bulk-action`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ record_ids: selectedLeads, action: 'mark_follow_up', payload: {} })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.updated} lead(s) marked for follow-up`);
        setSelectedLeads([]);
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Bulk follow-up update failed');
      }
    } catch (error) {
      toast.error('Error during bulk follow-up update');
    } finally {
      setMarkingFollowUp(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('keyword', searchQuery);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      await downloadFile(`${API}/phase9/leads/export?${params}`, user.token, `Leads_Export_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error(error.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const handleSelectLead = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const getScoreBadgeClass = (label) => {
    switch (label) {
      case 'Hot': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Warm': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Cold': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Bad Fit': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  return (
    <div className="page-stack" data-testid="lead-database">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9A — Lead Database</p>
          <h2>Lead Database</h2>
          <div className="hero-stats">
            <span>{leads.length} Loaded</span>
            <span>{selectedLeads.length} Selected</span>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            className="primary-btn"
            onClick={handleBulkScore}
            disabled={selectedLeads.length === 0}
            data-testid="bulk-score-btn"
          >
            <Target size={16} />
            Bulk Score ({selectedLeads.length})
          </Button>
          {['Master Administrator', 'Sales Manager', 'Sales Representative'].includes(user?.role) && (
            <Button
              className="ghost-btn"
              onClick={() => setEnrollOpen(true)}
              disabled={selectedLeads.length === 0}
              data-testid="bulk-enroll-sequence-btn"
            >
              <Mail size={16} />
              Enroll in sequence ({selectedLeads.length})
            </Button>
          )}
          {user?.role === 'Master Administrator' && (
            <>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-2"
                data-testid="bulk-assign-select"
              >
                <option value="">Assign to...</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
              <Button
                className="ghost-btn"
                onClick={handleBulkAssign}
                disabled={selectedLeads.length === 0 || !assigneeId || assigning}
                data-testid="bulk-assign-btn"
              >
                <UserPlus size={16} />
                {assigning ? 'Assigning...' : 'Assign'}
              </Button>
              <Button
                className="ghost-btn"
                onClick={handleBulkFollowUp}
                disabled={selectedLeads.length === 0 || markingFollowUp}
                data-testid="bulk-follow-up-btn"
              >
                <Clock size={16} />
                {markingFollowUp ? 'Updating...' : 'Mark Follow-Up'}
              </Button>
            </>
          )}
        </div>
      </section>

      {/* Toolbar */}
      <section className="glass-panel">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchLeads()}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="database-search-input"
            />
          </div>
          <Button className="ghost-btn" onClick={fetchLeads}>
            <Filter size={16} />
            Filter
          </Button>
          {activeFilterCount > 0 && (
            <Button className="ghost-btn" onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }} data-testid="clear-filters-btn">
              Clear filters ({activeFilterCount})
            </Button>
          )}
          <Button className="ghost-btn" onClick={handleExport} disabled={exporting} data-testid="lead-database-export-btn">
            <Download size={16} />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
        <div className="flex flex-wrap gap-3 mt-4" data-testid="lead-filters">
          <select value={filters.score_label} onChange={(e) => updateFilter('score_label', e.target.value)} className={SELECT_CLASS} data-testid="filter-score-label">
            <option value="">Any score</option>
            <option value="Hot">Hot</option>
            <option value="Warm">Warm</option>
            <option value="Cold">Cold</option>
            <option value="Bad Fit">Bad Fit</option>
          </select>
          <select value={filters.has_website} onChange={(e) => updateFilter('has_website', e.target.value)} className={SELECT_CLASS} data-testid="filter-has-website">
            <option value="">Any website</option>
            <option value="true">Has website</option>
            <option value="false">No website</option>
          </select>
          <select value={filters.min_rating} onChange={(e) => updateFilter('min_rating', e.target.value)} className={SELECT_CLASS} data-testid="filter-min-rating">
            <option value="">Any rating</option>
            <option value="4.5">4.5+ stars</option>
            <option value="4">4.0+ stars</option>
            <option value="3.5">3.5+ stars</option>
          </select>
          <Input
            placeholder="City"
            value={filters.city}
            onChange={(e) => updateFilter('city', e.target.value)}
            className="w-40 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            data-testid="filter-city"
          />
          <Input
            placeholder="State"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
            className="w-28 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            data-testid="filter-state"
          />
        </div>
      </section>

      {/* Table */}
      <section className="glass-panel overflow-x-auto">
        {loading ? (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full bg-white/10" />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-white/60">No leads found</p>
          </div>
        ) : (
          <table className="w-full" data-testid="leads-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-3">
                  <Checkbox
                    checked={selectedLeads.length === leads.length}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">
                  <button className="flex items-center gap-1 hover:text-white/80">
                    Clinic Name
                    <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">Location</th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">Email</th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">Phone</th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">Score</th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">Category</th>
                <th className="text-left p-3 text-[11px] uppercase tracking-[0.08em] text-white/60">Stage</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  onClick={() => onNavigate('detail', lead)}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <td className="p-3">
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => handleSelectLead(lead.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="p-3 text-white/90 font-medium">{lead.clinic_name}</td>
                  <td className="p-3 text-white/70 text-sm">{lead.city}, {lead.state}</td>
                  <td className="p-3 text-white/70 text-sm">{lead.email || '—'}</td>
                  <td className="p-3 text-white/70 text-sm">{lead.phone || '—'}</td>
                  <td className="p-3">
                    {lead.ai_lead_score !== null && lead.ai_lead_score !== undefined ? (
                      <Badge className={`${getScoreBadgeClass(lead.ai_lead_score_label)} text-xs`}>
                        {lead.ai_lead_score}
                      </Badge>
                    ) : (
                      <span className="text-white/40 text-xs">Not scored</span>
                    )}
                  </td>
                  <td className="p-3 text-white/70 text-sm">{lead.category || 'Med Spa'}</td>
                  <td className="p-3 text-white/70 text-sm">{lead.status || 'Lead Found'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!loading && leads.length > 0 && (
          <div className="flex justify-between items-center pt-4 border-t border-white/10 mt-4">
            <p className="text-sm text-white/60">Page {page}</p>
            <div className="flex gap-2">
              <Button
                className="ghost-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft size={16} />
                Previous
              </Button>
              <Button
                className="ghost-btn"
                onClick={() => setPage(p => p + 1)}
                disabled={leads.length < limit}
              >
                Next
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </section>
      <EnrollInSequenceDialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        token={user.token}
        leadIds={selectedLeads}
        onDone={() => setSelectedLeads([])}
      />
    </div>
  );
};

export default LeadDatabase;
